import { useRef, useState, DragEvent } from 'react';
import { useCanvasOperations } from '../../hooks';
import { useImageLibrary } from '../../hooks/useImageLibrary';
import { t } from '../../i18n';
import styles from './UploadPanel.module.css';

interface ConvertFileResponse {
  success: boolean;
  error?: string;
  convertedUrl?: string;
  originalUrl?: string;
  originalExtension?: string;
  needsClientConversion?: boolean;
  pdfUrl?: string;
}

interface UploadPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UploadPanel = ({ isOpen, onClose }: UploadPanelProps) => {
  const { addImageFromFile, addImageFromUrl } = useCanvasOperations();
  const { uploads, isLoading: libraryLoading, addUpload, error: libraryError, clearError, refetch } = useImageLibrary();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const designFileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'library'>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');

  const handleImageClick = () => {
    imageInputRef.current?.click();
  };

  const handleDesignFileClick = () => {
    designFileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleImageFile(file);
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleDesignFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleDesignFile(file);
    }
    if (designFileInputRef.current) {
      designFileInputRef.current.value = '';
    }
  };

  const handleImageFile = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(60);
    setUploadMessage(t('design.upload.adding', 'Adding to canvas…'));

    try {
      // Renders the local file on the canvas INSTANTLY, then uploads to R2 in the
      // background and saves it to the library once the permanent URL is ready.
      await addImageFromFile(file, (url) => {
        addUpload(url, url, file.name);
      });

      // Image is already visible — close right away (no waiting on the upload).
      setUploadProgress(100);
      setIsUploading(false);
      onClose();
    } catch (err) {
      console.error('Image upload failed:', err);
      setIsUploading(false);
      alert(t('design.upload.failed', 'Failed to add image'));
    }
  };

  const handleDesignFile = async (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    setIsUploading(true);
    setUploadProgress(15);
    setUploadMessage(`${extension?.toUpperCase()} file detected`);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('extension', extension || '');

      setUploadProgress(30);
      setUploadMessage(`Converting ${extension?.toUpperCase()} file...`);

      const response = await fetch('/api/convert-file', {
        method: 'POST',
        body: formData,
      });

      const data: ConvertFileResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Conversion failed');
      }

      setUploadProgress(60);
      setUploadMessage('Loading converted image...');

      // If PSD/PDF - converted to PNG directly
      // Use addImageFromUrl with originalUrl so we keep the original PSD/AI file for download
      if (data.convertedUrl) {
        setUploadProgress(85);
        setUploadMessage('Adding to canvas...');

        // Pass both the converted URL (for display) and original URL (for vendor download)
        addImageFromUrl(data.convertedUrl, data.originalUrl);
      }
      // If AI/EPS - needs client-side PDF conversion
      else if (data.needsClientConversion && data.pdfUrl) {
        setUploadMessage('Converting PDF preview...');
        // TODO: Implement PDF.js conversion
        // For now, the original AI file is already saved via data.originalUrl
        alert('AI/EPS files require PDF.js library. This will be added in the next update.');
      }

      setUploadProgress(100);
      setUploadMessage('Complete!');
      setTimeout(() => {
        setIsUploading(false);
        onClose();
      }, 800);
    } catch (err) {
      console.error('Design file conversion failed:', err);
      setIsUploading(false);
      const message = err instanceof Error ? err.message : 'Failed to convert file';
      alert(message);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleLibraryImageClick = (url: string) => {
    addImageFromUrl(url);
    onClose();
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    const designFileExts = ['pdf', 'ai', 'eps', 'psd'];

    if (designFileExts.includes(extension || '')) {
      await handleDesignFile(file);
    } else {
      await handleImageFile(file);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>
        {/* Tabs */}
        <div className={styles.tabs} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'upload'}
            aria-controls="upload-panel"
            className={`${styles.tab} ${activeTab === 'upload' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload New
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'library'}
            aria-controls="library-panel"
            className={`${styles.tab} ${activeTab === 'library' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('library')}
          >
            My Uploads
          </button>
        </div>

        {activeTab === 'upload' ? (
          <div id="upload-panel" role="tabpanel">
            {/* Image Upload Section */}
            <div
              className={`${styles.dropzone} ${isDragging ? styles.dragging : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <svg
                className={styles.uploadIcon}
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className={styles.dropText}>
                {isDragging ? t('design.upload.drop_here', 'Drop image here') : t('design.upload.drag_drop', 'Drag & drop image here')}
              </p>
              <p className={styles.divider}>{t('design.upload.or', 'or')}</p>
              <button className={styles.browseButton} onClick={handleImageClick}>
                {t('design.upload.browse', 'Browse Images')}
              </button>
              <p className={styles.formats}>JPG, PNG, SVG, GIF</p>
            </div>

            {/* Professional Files Section */}
            <div className={styles.professionalSection}>
              <div className={styles.sectionDivider}>{t('design.upload.professional', 'Professional Files')}</div>
              <button className={styles.designFileButton} onClick={handleDesignFileClick}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                {t('design.upload.upload_design_files', 'Upload PDF, AI, EPS, PSD')}
              </button>
              <p className={styles.note}>{t('design.upload.converted_note', 'Files will be converted for editing')}</p>
            </div>
          </div>
        ) : (
          /* Library Grid */
          <div id="library-panel" role="tabpanel" className={styles.libraryContent}>
            {libraryError && (
              <div className={styles.libraryError} role="alert">
                <span>{libraryError}</span>
                <button
                  className={styles.retryButton}
                  onClick={() => {
                    clearError();
                    refetch();
                  }}
                  aria-label={t('design.upload.retry_aria', 'Retry loading uploads')}
                >
                  {t('design.upload.retry', 'Retry')}
                </button>
              </div>
            )}
            {!libraryError && libraryLoading ? (
              <div className={styles.libraryLoading}>{t('design.loading', 'Loading...')}</div>
            ) : !libraryError && uploads.length === 0 ? (
              <div className={styles.emptyLibrary}>
                {t('design.upload.empty_library', 'No uploads yet. Upload an image to get started.')}
              </div>
            ) : !libraryError && (
              <div className={styles.libraryGrid} role="list">
                {uploads.map((upload) => (
                  <button
                    key={upload.id}
                    role="listitem"
                    className={styles.libraryThumbnail}
                    onClick={() => handleLibraryImageClick(upload.url)}
                    aria-label={`Add ${upload.filename} to canvas`}
                    title={upload.filename}
                  >
                    <img src={upload.thumbnailUrl} alt={upload.filename} loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className={styles.hiddenInput}
        />
        <input
          ref={designFileInputRef}
          type="file"
          accept=".pdf,.ai,.eps,.psd"
          onChange={handleDesignFileChange}
          className={styles.hiddenInput}
        />
      </div>

      {/* Upload Progress Overlay */}
      {isUploading && (
        <div className={styles.progressOverlay}>
          <div className={styles.progressModal}>
            <div className={styles.progressIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <h3 className={styles.progressTitle}>{t('design.upload.uploading', 'Uploading')}</h3>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className={styles.progressMessage}>{uploadMessage}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadPanel;
